import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Patient, PatientAnalytics, MedicalInfoUpdate } from '../../api/services/patients';
import { PatientFormData } from '../../types/patient';

export interface FieldErrors {
  [key: string]: string[];
}

export interface ErrorPayload {
  message: string;
  fieldErrors?: FieldErrors;
  status?: number;
}

interface PatientsState {
  allPatients: {
    data: Patient[] | null;
    loading: boolean;
    error: string | null;
  };
  patientDetails: {
    data: Patient | null;
    loading: boolean;
    error: string | null;
  };
  patientAnalytics: {
    data: PatientAnalytics | null;
    loading: boolean;
    error: string | null;
  };
  createPatient: {
    loading: boolean;
    error: ErrorPayload | null;
    success: boolean;
  };
  updatePatient: {
    loading: boolean;
    error: ErrorPayload | null;
    success: boolean;
  };
  updateMedicalInfo: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  deletePatient: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
}

// Initial state
const initialState: PatientsState = {
  allPatients: {
    data: null,
    loading: false,
    error: null,
  },
  patientDetails: {
    data: null,
    loading: false,
    error: null,
  },
  patientAnalytics: {
    data: null,
    loading: false,
    error: null,
  },
  createPatient: {
    loading: false,
    error: null,
    success: false,
  },
  updatePatient: {
    loading: false,
    error: null,
    success: false,
  },
  updateMedicalInfo: {
    loading: false,
    error: null,
    success: false,
  },
  deletePatient: {
    loading: false,
    error: null,
    success: false,
  },
};

const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    // Fetch all patients
    fetchPatients: state => {
      state.allPatients.loading = true;
      state.allPatients.error = null;
    },
    fetchPatientsSuccess: (state, action: PayloadAction<Patient[]>) => {
      state.allPatients.data = action.payload;
      state.allPatients.loading = false;
      state.allPatients.error = null;
    },
    fetchPatientsFailure: (state, action: PayloadAction<string>) => {
      state.allPatients.loading = false;
      state.allPatients.error = action.payload;
    },

    // Fetch patient details
    fetchPatientDetails: (state, action: PayloadAction<number | string>) => {
      state.patientDetails.loading = true;
      state.patientDetails.error = null;
    },
    fetchPatientDetailsSuccess: (state, action: PayloadAction<Patient>) => {
      state.patientDetails.data = action.payload;
      state.patientDetails.loading = false;
      state.patientDetails.error = null;
    },
    fetchPatientDetailsFailure: (state, action: PayloadAction<string>) => {
      state.patientDetails.loading = false;
      state.patientDetails.error = action.payload;
    },

    // Create patient
    createPatient: (
      state,
      action: PayloadAction<{ data: PatientFormData; cb: (patientId: string) => void }>
    ) => {
      state.createPatient.loading = true;
      state.createPatient.error = null;
      state.createPatient.success = false;
    },
    createPatientSuccess: state => {
      state.createPatient.loading = false;
      state.createPatient.error = null;
      state.createPatient.success = true;
    },
    createPatientFailure: (state, action: PayloadAction<ErrorPayload>) => {
      state.createPatient.loading = false;
      state.createPatient.error = action.payload;
      state.createPatient.success = false;
    },

    // Update patient
    updatePatient: (
      state,
      action: PayloadAction<{ id: number | string; data: Partial<PatientFormData> }>
    ) => {
      state.updatePatient.loading = true;
      state.updatePatient.error = null;
      state.updatePatient.success = false;
    },
    updatePatientSuccess: state => {
      state.updatePatient.loading = false;
      state.updatePatient.error = null;
      state.updatePatient.success = true;
    },
    updatePatientFailure: (state, action: PayloadAction<ErrorPayload>) => {
      state.updatePatient.loading = false;
      state.updatePatient.error = action.payload;
      state.updatePatient.success = false;
    },

    // Update medical info
    updateMedicalInfo: (
      state,
      action: PayloadAction<{ id: number | string; data: MedicalInfoUpdate }>
    ) => {
      state.updateMedicalInfo.loading = true;
      state.updateMedicalInfo.error = null;
      state.updateMedicalInfo.success = false;
    },
    updateMedicalInfoSuccess: state => {
      state.updateMedicalInfo.loading = false;
      state.updateMedicalInfo.error = null;
      state.updateMedicalInfo.success = true;
    },
    updateMedicalInfoFailure: (state, action: PayloadAction<string>) => {
      state.updateMedicalInfo.loading = false;
      state.updateMedicalInfo.error = action.payload;
      state.updateMedicalInfo.success = false;
    },

    // Delete patient
    deletePatient: (state, action: PayloadAction<number | string>) => {
      state.deletePatient.loading = true;
      state.deletePatient.error = null;
      state.deletePatient.success = false;
    },
    deletePatientSuccess: state => {
      state.deletePatient.loading = false;
      state.deletePatient.error = null;
      state.deletePatient.success = true;
    },
    deletePatientFailure: (state, action: PayloadAction<string>) => {
      state.deletePatient.loading = false;
      state.deletePatient.error = action.payload;
      state.deletePatient.success = false;
    },

    // Fetch patient analytics
    fetchPatientAnalytics: state => {
      state.patientAnalytics.loading = true;
      state.patientAnalytics.error = null;
    },
    fetchPatientAnalyticsSuccess: (state, action: PayloadAction<PatientAnalytics>) => {
      state.patientAnalytics.data = action.payload;
      state.patientAnalytics.loading = false;
      state.patientAnalytics.error = null;
    },
    fetchPatientAnalyticsFailure: (state, action: PayloadAction<string>) => {
      state.patientAnalytics.loading = false;
      state.patientAnalytics.error = action.payload;
    },

    // Reset success flags
    resetPatientSuccess: state => {
      state.createPatient.success = false;
      state.updatePatient.success = false;
      state.updateMedicalInfo.success = false;
      state.deletePatient.success = false;
    },
    resetErrors: state => {
      state.createPatient.error = null;
      state.updatePatient.error = null;
      state.updateMedicalInfo.error = null;
      state.deletePatient.error = null;
    },
  },
});

export const {
  fetchPatients,
  fetchPatientsSuccess,
  fetchPatientsFailure,
  fetchPatientDetails,
  fetchPatientDetailsSuccess,
  fetchPatientDetailsFailure,
  createPatient,
  createPatientSuccess,
  createPatientFailure,
  updatePatient,
  updatePatientSuccess,
  updatePatientFailure,
  updateMedicalInfo,
  updateMedicalInfoSuccess,
  updateMedicalInfoFailure,
  deletePatient,
  deletePatientSuccess,
  deletePatientFailure,
  fetchPatientAnalytics,
  fetchPatientAnalyticsSuccess,
  fetchPatientAnalyticsFailure,
  resetPatientSuccess,
  resetErrors,
} = patientsSlice.actions;

export default patientsSlice.reducer;
